/**
 * useOrganizations Hook
 * 사업자 관리 관련 커스텀 훅
 */

import { useState, useCallback, useEffect } from 'react';
import { OrganizationRepository } from '../repositories/OrganizationRepository';
import { OrganizationService } from '../services/OrganizationService';
import { Organization, ServiceType } from '../models';

const organizationRepository = new OrganizationRepository();
const organizationService = new OrganizationService(organizationRepository);

export const useOrganizations = (serviceType?: ServiceType) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [pendingCount, setPendingCount] = useState(0);

  /**
   * 승인 대기 목록 조회
   */
  const fetchPending = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await organizationService.getPendingOrganizations(
        serviceType
      );
      setOrganizations(data);
      setPendingCount(data.length);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : '조회에 실패했습니다.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [serviceType]);

  /**
   * 승인 완료 목록 조회
   */
  const fetchApproved = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await organizationService.getApprovedOrganizations(
        serviceType
      );
      setOrganizations(data);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : '조회에 실패했습니다.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [serviceType]);

  /**
   * 거부된 목록 조회
   */
  const fetchRejected = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await organizationService.getRejectedOrganizations(
        serviceType
      );
      setOrganizations(data);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : '조회에 실패했습니다.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [serviceType]);

  /**
   * 전체 목록 조회
   */
  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await organizationService.getAllOrganizations(serviceType);
      setOrganizations(data);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : '조회에 실패했습니다.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [serviceType]);

  /**
   * 사업자 승인
   */
  const approve = useCallback(
    async (organizationId: string, reason?: string): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        await organizationService.approveOrganization({
          organizationId,
          reason,
        });

        // 목록 새로고침
        await fetchPending();
        return true;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : '승인에 실패했습니다.';
        setError(errorMessage);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [fetchPending]
  );

  /**
   * 사업자 거부
   */
  const reject = useCallback(
    async (organizationId: string, reason: string): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        await organizationService.rejectOrganization({
          organizationId,
          reason,
        });

        // 목록 새로고침
        await fetchPending();
        return true;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : '거부에 실패했습니다.';
        setError(errorMessage);
        return false;
      } finally {
        setLoading(false);
      }
    },
    [fetchPending]
  );

  /**
   * 승인 대기 건수 갱신
   */
  const refreshPendingCount = useCallback(async () => {
    try {
      const count = await organizationService.getPendingCount(serviceType);
      setPendingCount(count);
    } catch (err) {
      console.error('Failed to refresh pending count:', err);
    }
  }, [serviceType]);

  /**
   * 검색
   */
  const search = useCallback(
    (query: string) => {
      const filtered = organizationService.searchOrganizations(
        organizations,
        query
      );
      setOrganizations(filtered);
    },
    [organizations]
  );

  /**
   * 사업자 사용 중지
   */
  const suspend = useCallback(
    async (organizationId: string, reason: string): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        await organizationService.suspendOrganization(organizationId, reason);
        return true;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : '사용 중지에 실패했습니다.';
        setError(errorMessage);
        return false;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * 사업자 재활성화
   */
  const reactivate = useCallback(
    async (organizationId: string): Promise<boolean> => {
      setLoading(true);
      setError(null);

      try {
        await organizationService.reactivateOrganization(organizationId);
        return true;
      } catch (err) {
        const errorMessage =
          err instanceof Error ? err.message : '재활성화에 실패했습니다.';
        setError(errorMessage);
        return false;
      } finally {
        setLoading(false);
      }
    },
    []
  );

  /**
   * 사용 중지된 사업자 목록 조회
   */
  const fetchSuspended = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await organizationService.getSuspendedOrganizations();
      setOrganizations(data);
    } catch (err) {
      const errorMessage =
        err instanceof Error ? err.message : '조회에 실패했습니다.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  // 마운트 시 승인 대기 건수 조회
  useEffect(() => {
    refreshPendingCount();
  }, [refreshPendingCount]);

  return {
    organizations,
    pendingCount,
    loading,
    error,
    fetchPending,
    fetchApproved,
    fetchRejected,
    fetchSuspended,
    fetchAll,
    approve,
    reject,
    suspend,
    reactivate,
    refreshPendingCount,
    search,
  };
};
